import { ArrowLeft, FileText, Star } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

const STATUS_FLOW = ['Placed', 'Confirmed', 'Out for Delivery', 'Delivered']

type OrderItem = {
  id: number
  product_name: string
  weight_value: number
  weight_unit: string
  quantity: number
  price: number
  image_url: string
}

type Order = {
  id: number
  orderNumber: string
  status: string
  nextStatus: string | null
  full_name: string
  mobile: string
  recipient: string
  line1: string
  line2: string
  city: string
  state: string
  pincode: string
  subtotal: number
  delivery_charge: number
  discount: number
  offer_code: string | null
  total: number
  created_at: string
  items: OrderItem[]
  payment_mode: 'cod' | 'online'
  payment_status: 'pending' | 'paid' | 'failed'
  phonepe_transaction_id: string | null
}

type ItemRating = {
  order_item_id: number
  product_id: number
  product_name: string
  rating: number
  message: string | null
}

type OrderRating = {
  id: number
  rating: number
  message: string | null
  full_name: string
  mobile: string
  created_at: string
  itemRatings: ItemRating[]
}

function StarDisplay({ value, size = 16 }: { value: number; size?: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={size}
          className={s <= Math.round(value) ? 'fill-amber-400 stroke-amber-400' : 'fill-none stroke-stone-300'}
        />
      ))}
    </span>
  )
}

export function OrderDetailPage() {
  const { id } = useParams()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [message, setMessage] = useState('')
  const [showCancel, setShowCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [rating, setRating] = useState<OrderRating | null | undefined>(undefined)
  const [askingSent, setAskingSent] = useState(false)
  const [askingMsg, setAskingMsg] = useState('')

  const loadOrder = useCallback(() => {
    setLoading(true)
    return api.get(`/admin/orders/${id}`)
      .then(({ data }) => setOrder(data.order))
      .finally(() => setLoading(false))
  }, [id])

  const loadRating = useCallback(() => {
    return api.get(`/admin/orders/${id}/rating`)
      .then(({ data }) => setRating(data.rating ?? null))
      .catch(() => setRating(null))
  }, [id])

  useEffect(() => {
    loadOrder()
  }, [loadOrder])

  // Load rating only when order is Delivered
  useEffect(() => {
    if (!order || order.status !== 'Delivered') {
      setRating(undefined)
      return
    }
    loadRating()
  }, [loadRating, order?.status])

  useEffect(() => {
    const handleRefresh = (event: Event) => {
      const orderId = (event as CustomEvent<{ orderId?: number | null }>).detail?.orderId
      if (orderId && String(orderId) !== String(id)) return
      loadOrder()
      if (order?.status === 'Delivered') loadRating()
    }

    window.addEventListener('seasonly-admin-order-refresh', handleRefresh)
    return () => window.removeEventListener('seasonly-admin-order-refresh', handleRefresh)
  }, [id, loadOrder, loadRating, order?.status])

  const advanceStatus = async () => {
    if (!order?.nextStatus) return
    setUpdating(true)
    setMessage('')
    try {
      await api.patch(`/admin/orders/${id}/status`, { status: order.nextStatus })
      const next = STATUS_FLOW[STATUS_FLOW.indexOf(order.nextStatus) + 1] ?? null
      setOrder(prev => prev ? { ...prev, status: prev.nextStatus!, nextStatus: next } : prev)
      setMessage(order.nextStatus)
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Failed to update status')
    } finally {
      setUpdating(false)
    }
  }

  const confirmCancel = async () => {
    setCancelling(true)
    try {
      await api.patch(`/admin/orders/${id}/cancel`)
      setOrder(prev => prev ? { ...prev, status: 'Cancelled', nextStatus: null } : prev)
      setShowCancel(false)
      setMessage('')
    } catch (err: any) {
      setMessage(err?.response?.data?.message || 'Failed to cancel order')
    } finally {
      setCancelling(false)
    }
  }

  if (loading) return <p className="p-6 text-sm text-stone-500">Loading…</p>
  if (!order) return <p className="p-6 text-sm text-red-600">Order not found.</p>

  const isCancelled = order.status === 'Cancelled'
  const activeIndex = STATUS_FLOW.indexOf(order.status)

  return (
    <div className="space-y-6">
      <PageTitle
        title={order.orderNumber}
        subtitle={`Placed on ${new Date(order.created_at).toLocaleDateString('en-IN')}`}
        action={
          <div className="flex gap-2">
            <NavLink to="/orders"><Button icon={ArrowLeft} variant="soft">Back</Button></NavLink>
            <Button icon={FileText} variant="soft">Invoice</Button>
          </div>
        }
      />

      {/* Status card */}
      <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-bold text-leaf-dark">Order Status</h2>
          <StatusPill value={order.status} />
        </div>

        {isCancelled ? (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
            This order has been cancelled.
          </div>
        ) : (
          <div className="relative flex items-start justify-between">
            {STATUS_FLOW.map((s, i) => {
              const done = activeIndex >= i
              const isLast = i === STATUS_FLOW.length - 1
              return (
                <div key={s} className="relative flex flex-1 flex-col items-center">
                  {!isLast && (
                    <div className="absolute left-1/2 top-3.5 h-px w-full border-t-2 border-dashed"
                      style={{ borderColor: activeIndex > i ? '#2d6a4f' : '#d6d3d1' }} />
                  )}
                  <div className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-bold ${done ? 'border-leaf-dark bg-leaf-dark text-white' : 'border-stone-300 bg-white text-stone-400'}`}>
                    {i + 1}
                  </div>
                  <p className={`mt-2 text-center text-xs font-semibold ${done ? 'text-leaf-dark' : 'text-stone-400'}`}>{s}</p>
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {!isCancelled && order.nextStatus && (
              <Button onClick={advanceStatus} disabled={updating}>
                {updating ? 'Updating…' : `Mark as ${order.nextStatus}`}
              </Button>
            )}
            {message && (
              <p className={`text-sm font-medium ${STATUS_FLOW.includes(message) ? 'text-green-600' : 'text-red-600'}`}>
                {message}
              </p>
            )}
          </div>
          {!isCancelled && order.status !== 'Delivered' && (
            <button onClick={() => setShowCancel(true)} disabled={updating} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
              Cancel Order
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Items */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-bold text-leaf-dark">Order Items</h2>
          {order.items.map(item => (
            <div key={item.id} className="flex items-center gap-3 py-2">
              {item.image_url && <img src={item.image_url} alt={item.product_name} className="h-12 w-12 rounded-lg object-cover" />}
              <div className="flex-1">
                <p className="text-sm font-semibold text-stone-900">{item.product_name}</p>
                <p className="text-xs text-stone-500">{item.weight_value} {item.weight_unit} × {item.quantity}</p>
              </div>
              <p className="text-sm font-semibold">₹{item.price * item.quantity}</p>
            </div>
          ))}
          <div className="mt-3 space-y-1 border-t border-stone-100 pt-3 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span>₹{order.subtotal}</span></div>
            <div className="flex justify-between"><span className="text-stone-500">Delivery</span><span>{Number(order.delivery_charge) === 0 ? 'Free' : `₹${order.delivery_charge}`}</span></div>
            {Number(order.discount) > 0 && <div className="flex justify-between text-green-600"><span>Discount ({order.offer_code})</span><span>-₹{order.discount}</span></div>}
            <div className="flex justify-between font-bold"><span>Total</span><span>₹{order.total}</span></div>
          </div>
        </div>

        {/* Customer & Address */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-bold text-leaf-dark">Customer</h2>
          <p className="text-sm font-semibold">{order.full_name || '—'}</p>
          <p className="text-sm text-stone-500">+91 {order.mobile}</p>
          <h2 className="mb-2 mt-5 font-bold text-leaf-dark">Delivery Address</h2>
          <p className="text-sm font-semibold">{order.recipient}</p>
          <p className="text-sm text-stone-500">{order.line1}, {order.line2}, {order.city}, {order.state} - {order.pincode}</p>
        </div>
      </div>

      {/* Payment Info */}
      <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 font-bold text-leaf-dark">Payment</h2>
        <div className="flex flex-wrap gap-6 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-1">Mode</p>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${order.payment_mode === 'online' ? 'bg-blue-50 text-blue-700' : 'bg-stone-100 text-stone-600'}`}>
              {order.payment_mode === 'online' ? 'Online (PhonePe)' : 'Cash on Delivery'}
            </span>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-1">Status</p>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              order.payment_status === 'paid' ? 'bg-green-50 text-green-700' :
              order.payment_status === 'failed' ? 'bg-red-50 text-red-700' :
              'bg-amber-50 text-amber-700'
            }`}>
              {order.payment_status === 'paid' ? 'Paid' : order.payment_status === 'failed' ? 'Failed' : 'Pending'}
            </span>
          </div>
          {order.phonepe_transaction_id && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-stone-400 mb-1">Transaction ID</p>
              <p className="font-mono text-xs text-stone-600">{order.phonepe_transaction_id}</p>
            </div>
          )}
        </div>
      </div>

      {/* Rating card — only for Delivered orders */}
      {order.status === 'Delivered' && (
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Star size={18} className="fill-amber-400 stroke-amber-400" />
            <h2 className="font-bold text-leaf-dark">Customer Rating</h2>
          </div>

          {rating === undefined && (
            <p className="text-sm text-stone-400">Loading rating…</p>
          )}

          {rating === null && (
            <div className="space-y-3">
              <div className="rounded-lg bg-stone-50 px-4 py-3 text-sm text-stone-500">
                No rating submitted yet for this order.
              </div>
              {!askingSent ? (
                <button
                  className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                  onClick={async () => {
                    setAskingMsg('')
                    try {
                      await api.post(`/admin/orders/${id}/ask-rating`)
                      setAskingSent(true)
                      setAskingMsg('Rating request sent to customer.')
                    } catch (err: any) {
                      setAskingMsg(err?.response?.data?.message || 'Failed to send notification')
                    }
                  }}
                >
                  <Star size={15} className="fill-amber-500 stroke-amber-500" />
                  Ask customer to rate
                </button>
              ) : (
                <div className="flex items-center gap-2 rounded-lg bg-green-50 px-4 py-2.5 text-sm font-semibold text-green-700">
                  <Star size={15} className="fill-green-500 stroke-green-500" />
                  {askingMsg}
                </div>
              )}
              {askingMsg && !askingSent && (
                <p className="text-sm font-medium text-red-600">{askingMsg}</p>
              )}
            </div>
          )}

          {rating && (
            <div className="space-y-4">
              {/* Overall */}
              <div className="rounded-lg border border-stone-100 bg-stone-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-stone-400">Overall</p>
                    <div className="mt-1 flex items-center gap-2">
                      <StarDisplay value={rating.rating} size={18} />
                      <span className="text-sm font-bold text-stone-700">{rating.rating}/5</span>
                    </div>
                  </div>
                  <div className="text-right text-xs text-stone-400">
                    {new Date(rating.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                {rating.message && (
                  <p className="mt-2 text-sm italic text-stone-600">"{rating.message}"</p>
                )}
              </div>

              {/* Per-item ratings */}
              {rating.itemRatings.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-400">Item Ratings</p>
                  <div className="space-y-2">
                    {rating.itemRatings.map((ir) => (
                      <div key={ir.order_item_id} className="flex items-start gap-3 rounded-lg border border-stone-100 bg-white p-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-stone-800 truncate">{ir.product_name}</p>
                          <div className="mt-1 flex items-center gap-2">
                            <StarDisplay value={ir.rating} size={14} />
                            <span className="text-xs font-semibold text-stone-500">{ir.rating}/5</span>
                          </div>
                          {ir.message && (
                            <p className="mt-1 text-xs italic text-stone-500">"{ir.message}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cancel confirmation modal */}
      {showCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-stone-900">Cancel this order?</h3>
            <p className="mt-2 text-sm text-stone-500">This action cannot be undone. The order status will be set to Cancelled.</p>
            <div className="mt-5 flex gap-3">
              <button onClick={() => setShowCancel(false)} className="flex-1 rounded-lg border border-stone-200 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50">
                Keep Order
              </button>
              <button onClick={confirmCancel} disabled={cancelling} className="flex-1 rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
